import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { PixiSettings } from "../../../../pixiSettings";

// Device-specific scale multipliers for the popup
const DEVICE_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: { scale: 1.0 },
    [PixiSettings.ORIENTATION.LANDSCAPE]: { scale: 0.8 },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: { scale: 1.0 },
    [PixiSettings.ORIENTATION.LANDSCAPE]: { scale: 0.9 },
  },
  [PixiSettings.DEVICE.DESKTOP]: { scale: 0.8 },
};

// Text slot names from the spine
const SLOTS = {
  TITLE: "txt_slot_bonus_buy_top",
  DESCRIPTION: "txt_slot_text",
  BUTTON: "txt_slot_bonus_buy_button",
};

/**
 * BuyBonusPopupController — spine-based buy bonus popup.
 *
 * Lifecycle:
 * 1. constructor → initializeSpine (create, addChild, alpha 0.001, warmUp, initializeTexts)
 * 2. show(bonusCost) → overlay fadeIn + set animation at alpha 0.001  → rAF reveal (alpha=1)
 * 3. hide(cb) → play "out" → overlay fade out → cb
 * 4. cleanup()
 *
 * KEY FIX: show() sets animation while still at alpha 0.001, then reveals on next frame
 * via requestAnimationFrame — same pattern as FreeSpinSceneTransitionController.playAnimation()
 * This prevents first-frame jitter caused by GPU compiling shaders for the new pose.
 */
class BuyBonusPopupController {
  constructor(container, dimensionsContext, dispatch, overlayRef, t) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.overlayRef = overlayRef;
    this.t = t;
    this.dimensions = dimensionsContext.dimensions;

    this.spine = null;
    this.isPlaying = false;
    this.initialized = false;
    this.currentTimeout = null;
    this._showRAF = null;

    // Text elements — created once in initializeTexts(), reused across show/hide cycles
    this.titleContainer = null;
    this.descContainer = null;
    this.buttonContainer = null;
    this.titleText = null;
    this.descText = null;
    this.buttonText = null;

    this._availableAnimationsCache = null;

    this.initializeSpine();
  }

  // =========================================================================
  // SCALING & DIMENSIONS
  // =========================================================================

  getDeviceConfig() {
    if (!this.dimensionsContext) return { scale: 1 };
    const { device, orientation } = this.dimensionsContext;
    const cfg = DEVICE_CONFIG[device];
    return cfg?.[orientation] ?? cfg ?? { scale: 1 };
  }

  applyScaling() {
    if (!this.spine?.scale || !this.spine.parent) return;

    let scale = this.dimensionsContext?.uiSymbolScale || 1;
    scale = Math.max(0.1, Math.min(scale, 2));

    const deviceConfig = this.getDeviceConfig();
    scale *= deviceConfig.scale || 1;

    try {
      this.spine.scale.set(scale);
    } catch {
      // Silent fail
    }
  }

  updateDimensions(dimensionsContext) {
    const dimensionsChanged =
      !this.dimensions ||
      this.dimensions.width !== dimensionsContext?.dimensions?.width ||
      this.dimensions.height !== dimensionsContext?.dimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;
    if (!dimensionsChanged) return;

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions;

    if (this.spine && this.initialized) {
      this.applyScaling();
    }
  }

  // =========================================================================
  // SPINE INITIALIZATION
  // =========================================================================

  initializeSpine() {
    try {
      this.spine = this.createSpine();
      if (!this.spine) return;

      if (
        !this.spine.skeleton ||
        !this.spine.state ||
        !this.spine.skeleton.data
      )
        return;

      this.container.addChild(this.spine);
      this.spine.alpha = 0.001;
      this.spine.zIndex = 20;

      this.applyScaling();

      if (this.spine.parent !== this.container) return;

      this.initialized = true;
      this._availableAnimationsCache = null;

      this.warmUp();

      // Create all text elements at init time (IntroPopupController pattern)
      this.initializeTexts();
    } catch (error) {
      // Silent fail
    }
  }

  createSpine() {
    try {
      const assets = PIXI.Assets.cache;
      if (
        !assets.has("bonus_buy_popup-skeleton") ||
        !assets.has("bonus_buy_popup-atlas")
      )
        return null;

      const spine = Spine.from({
        skeleton: "bonus_buy_popup-skeleton",
        atlas: "bonus_buy_popup-atlas",
      });

      if (!spine?.skeleton?.data?.animations || !spine?.state) return null;
      return spine;
    } catch (error) {
      return null;
    }
  }

  warmUp() {
    if (!this.spine?.state) return;
    try {
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.spine.state.setAnimation(0, animations[0], false);
        this.spine.state.update(0.016);
        this.spine.skeleton.updateWorldTransform();
      }
      this.spine.alpha = 0.001;
    } catch {
      // Silent fail
    }
  }

  getAvailableAnimations() {
    if (!this.spine?.skeleton?.data?.animations) return [];
    if (this._availableAnimationsCache !== null)
      return this._availableAnimationsCache;
    const animations = this.spine.skeleton.data.animations.map((a) => a.name);
    this._availableAnimationsCache = animations;
    return animations;
  }

  getSpineAnimationDuration(animationName) {
    if (!this.spine?.skeleton?.data?.animations) return 1000;
    try {
      const animation = this.spine.skeleton.data.animations.find(
        (a) => a.name === animationName,
      );
      if (animation?.duration) return Math.round(animation.duration * 1000);
      return 1000;
    } catch {
      return 1000;
    }
  }

  // =========================================================================
  // TEXT INITIALIZATION — all created at mount time
  // =========================================================================

  initializeTexts() {
    if (!this.spine?.skeleton) return;

    // Title — two-layer text matching BuyBonusController style
    const titleSlot = this.spine.skeleton.findSlot(SLOTS.TITLE);
    if (titleSlot) {
      this.titleContainer = new PIXI.Container();
      this.titleContainer.name = "buy_bonus_title_attached_to_slot";
      this.titleContainer.alpha = 1;
      this.titleContainer.zIndex = 1000;

      const titleString = "Buy Bonus";
      const titleFontSize = 52;

      const sharedTitleStyle = {
        fontFamily: "Noto Sans, Arial, sans-serif",
        fontSize: titleFontSize,
        fontWeight: "800",
        align: "center",
        letterSpacing: 1,
        padding: 10,
      };

      // Bottom layer — purple gradient with dark stroke
      this.titleShadow = new PIXI.Text(
        titleString,
        new PIXI.TextStyle({
          ...sharedTitleStyle,
          fill: ["#7b4cbf", "#5a2bb0", "#7b4cbf"],
          fillGradientType: 0,
          stroke: "#3a1878",
          strokeThickness: 4,
          lineJoin: "round",
        }),
      );
      this.titleShadow.resolution = 2;
      this.titleShadow.anchor.set(0.5);
      this.titleShadow.y = 3;

      // Top layer — white fill with purple stroke
      this.titleText = new PIXI.Text(
        titleString,
        new PIXI.TextStyle({
          ...sharedTitleStyle,
          fill: "#ffffff",
          stroke: "#3a1878",
          strokeThickness: 3,
          lineJoin: "round",
        }),
      );
      this.titleText.resolution = 2;
      this.titleText.anchor.set(0.5);

      this.titleContainer.addChild(this.titleShadow);
      this.titleContainer.addChild(this.titleText);
      this.spine.addSlotObject(SLOTS.TITLE, this.titleContainer);
    }

    // Description (cost)
    const descSlot = this.spine.skeleton.findSlot(SLOTS.DESCRIPTION);
    if (descSlot) {
      this.descContainer = new PIXI.Container();
      this.descContainer.name = "buy_bonus_desc_attached_to_slot";
      this.descContainer.alpha = 1;
      this.descContainer.zIndex = 1000;

      // Placeholder — text will be set later when content is decided
      const descString = "";
      const descStyle = new PIXI.TextStyle({
        fontFamily: "Noto Sans, Arial, sans-serif",
        fontSize: 24,
        fontWeight: "600",
        fill: "#ffffff",
        stroke: "#3a1878",
        strokeThickness: 2,
        align: "center",
        letterSpacing: 1,
        padding: 10,
        wordWrap: true,
        wordWrapWidth: 400,
      });

      this.descText = new PIXI.Text(descString, descStyle);
      this.descText.resolution = 2;
      this.descText.anchor.set(0.5);
      this.descContainer.addChild(this.descText);
      this.spine.addSlotObject(SLOTS.DESCRIPTION, this.descContainer);
    }

    // Button — two-layer text matching BuyBonusController style
    const buttonSlot = this.spine.skeleton.findSlot(SLOTS.BUTTON);
    if (buttonSlot) {
      this.buttonContainer = new PIXI.Container();
      this.buttonContainer.name = "buy_bonus_button_attached_to_slot";
      this.buttonContainer.alpha = 1;
      this.buttonContainer.zIndex = 1000;

      const buttonString = this.t("buy") || "BUY";
      const buttonFontSize = 48;

      const sharedButtonStyle = {
        fontFamily: "Noto Sans, Arial, sans-serif",
        fontSize: buttonFontSize,
        fontWeight: "800",
        align: "center",
        letterSpacing: 1,
        padding: 10,
      };

      // Bottom layer — purple gradient with dark stroke
      this.buttonShadow = new PIXI.Text(
        buttonString,
        new PIXI.TextStyle({
          ...sharedButtonStyle,
          fill: ["#7b4cbf", "#5a2bb0", "#7b4cbf"],
          fillGradientType: 0,
          stroke: "#3a1878",
          strokeThickness: 4,
          lineJoin: "round",
        }),
      );
      this.buttonShadow.resolution = 2;
      this.buttonShadow.anchor.set(0.5);
      this.buttonShadow.y = 3;

      // Top layer — white fill with purple stroke
      this.buttonText = new PIXI.Text(
        buttonString,
        new PIXI.TextStyle({
          ...sharedButtonStyle,
          fill: "#ffffff",
          stroke: "#3a1878",
          strokeThickness: 3,
          lineJoin: "round",
        }),
      );
      this.buttonText.resolution = 2;
      this.buttonText.anchor.set(0.5);

      this.buttonContainer.addChild(this.buttonShadow);
      this.buttonContainer.addChild(this.buttonText);
      this.spine.addSlotObject(SLOTS.BUTTON, this.buttonContainer);
    }
  }

  // =========================================================================
  // TEXT UPDATES
  // =========================================================================

  updateCost(bonusCost) {
    // Placeholder — will be used when description text is finalized
    if (!this.descText) return;
    // this.descText.text = `...`;
  }

  // =========================================================================
  // SHOW / HIDE
  // =========================================================================

  /**
   * Show the popup.
   * KEY: Set animation while still at alpha 0.001, reveal on NEXT FRAME via rAF.
   * (same pattern as FreeSpinSceneTransitionController.playAnimation)
   */
  show(bonusCost) {
    if (this.isPlaying || !this.spine?.state || !this.initialized) return;

    this.isPlaying = true;
    this.overlayRef.current?.fadeInBackground();

    if (this.spine && this.initialized) {
      this.applyScaling();
    }

    // Set animation while STILL HIDDEN (alpha 0.001) — lets GPU process new pose
    this.spine.state.clearTracks();
    this.spine.state.clearListeners();
    this.spine.state.apply(this.spine.skeleton);
    this.spine.skeleton.setToSetupPose();

    // Update cost text
    this.updateCost(bonusCost);

    const hasIn = this.spine.skeleton.data.animations.find(
      (a) => a.name === "in",
    );
    if (hasIn) {
      this.spine.state.setAnimation(0, "in", false);

      const inDuration = this.getSpineAnimationDuration("in");
      this.currentTimeout = setTimeout(() => {
        if (this.isPlaying && this.spine?.state) {
          this.spine.state.setAnimation(0, "idle", true);
        }
      }, inDuration);
    } else {
      this.spine.state.setAnimation(0, "idle", true);
    }

    // Show on NEXT FRAME — old warmup pose is gone, new animation starts fresh
    if (this._showRAF) cancelAnimationFrame(this._showRAF);
    this._showRAF = requestAnimationFrame(() => {
      this._showRAF = null;
      if (this.isPlaying && this.spine) {
        this.spine.alpha = 1;
      }
    });
  }

  hide(onComplete) {
    if (!this.isPlaying || !this.spine?.state) {
      if (onComplete) onComplete();
      return;
    }

    this.spine.state.clearListeners();

    const hasOut = this.spine.skeleton.data.animations.find(
      (a) => a.name === "out",
    );
    if (hasOut) {
      this.spine.state.setAnimation(0, "out", false);
      this.spine.state.addListener({
        complete: (entry) => {
          if (entry.animation?.name === "out" && this.isPlaying) {
            this.finishAnimation(onComplete);
          }
        },
      });
    } else {
      this.finishAnimation(onComplete);
    }
  }

  finishAnimation(onComplete) {
    if (!this.spine || !this.initialized) return;

    this.isPlaying = false;
    this.spine.alpha = 0.001;

    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }

    this.overlayRef.current?.fadeOutBackground(() => {
      if (onComplete) onComplete();
    });
  }

  // =========================================================================
  // BUTTON INTERACTIVITY
  // =========================================================================

  setButtonInteractive(onClick) {
    if (!this.buttonContainer) return;
    this.buttonContainer.eventMode = "static";
    this.buttonContainer.cursor = "pointer";
    this.buttonContainer.removeAllListeners?.();
    this.buttonContainer.on("pointertap", onClick);
  }

  // =========================================================================
  // CLEANUP
  // =========================================================================

  cleanup() {
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }

    // Cleanup text containers
    if (this.titleContainer) {
      this.titleContainer.destroy({ children: true });
      this.titleContainer = null;
      this.titleText = null;
      this.titleShadow = null;
    }
    if (this.descContainer) {
      this.descContainer.destroy({ children: true });
      this.descContainer = null;
      this.descText = null;
    }
    if (this.buttonContainer) {
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
      this.buttonText = null;
      this.buttonShadow = null;
    }

    if (this.spine) {
      this.spine.state?.clearListeners();
      this.spine.state?.clearTracks();
      if (this.container?.children.includes(this.spine)) {
        this.container.removeChild(this.spine);
      }
      this.spine.destroy();
      this.spine = null;
    }

    this.initialized = false;
  }
}

export default BuyBonusPopupController;
